import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { PlatformContext } from "~/hooks/use-platform";
import { usePreferencesStore } from "~/stores/use-preferences-store";

interface PlatformProviderProps {
  children: ReactNode;
}

const PLATFORMS = {
  DESKTOP: "desktop",
  WEB: "web",
  ANDROID: "android",
  IOS: "ios",
  PWA: "pwa",
} as const;

export type Platform = keyof typeof PLATFORMS;

function isVersionHigher(version: string, targetVersion: string): boolean {
  const versionParts: number[] = version.split(".").map(Number);
  const targetVersionParts: number[] = targetVersion.split(".").map(Number);

  for (
    let i = 0;
    i < Math.max(versionParts.length, targetVersionParts.length);
    i++
  ) {
    const versionPart: number = versionParts[i] ?? 0;
    const targetVersionPart: number = targetVersionParts[i] ?? 0;

    if (versionPart > targetVersionPart) {
      return true;
    } else if (versionPart < targetVersionPart) {
      return false;
    }
  }

  return false; // versions are equal
}

export function PlatformProvider({ children }: PlatformProviderProps) {
  const [platform, setPlatform] = useState<Platform>();

  const { fullScreen, version, baseStation, setVersion } = usePreferencesStore(
    useShallow((state) => ({
      fullScreen: state.fullScreen,
      version: state.version,
      setVersion: state.setVersion,
      baseStation: state.baseStation,
    })),
  );

  useEffect(() => {
    if (platform === "IOS" || platform === "ANDROID") {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          event: "base-station",
          data: {
            ip: baseStation,
          },
        }),
      );
    }
  }, [baseStation, platform]);

  useEffect(() => {
    if (window.vrodex?.getVersion) {
      const getVersion = async () => {
        const tempVersion = await window.vrodex.getVersion();

        setVersion(tempVersion);
      };
      getVersion().catch((err) => console.warn(err));
    }
  }, [setVersion]);

  useEffect(() => {
    console.log("PLATFORM PROVIDER");
    if (window.vrodex) {
      setPlatform("DESKTOP");
      if (isVersionHigher(version, "1.0.9")) {
        if (fullScreen) {
          if (window.vrodex.enterFullScreen) {
            void window.vrodex.enterFullScreen();
          }
        } else {
          if (window.vrodex.exitFullScreen) {
            void window.vrodex.exitFullScreen();
          }
        }
      }
    } else if (window.platform) {
      if (window.platform === "android") {
        setPlatform("ANDROID");
      } else if (window.platform === "ios") {
        setPlatform("IOS");
      }
    } else {
      if (
        /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
          navigator.userAgent,
        )
      ) {
        setPlatform("PWA");
      } else {
        setPlatform("WEB");
      }
    }
  }, [setPlatform, fullScreen, version]);

  const context = {
    platform,
  };

  return (
    <PlatformContext.Provider value={context}>
      {children}
    </PlatformContext.Provider>
  );
}
